import { FC, useEffect, useRef, useState } from "react";
import "./CSS/CalendarScreen.css";
import locationIcon from "../../assets/images/calendar-location-icon.svg";
import eventVideoIcon from "../../assets/images/event-video-icon.svg";
import CalendarStrip from "./Views/CalendarStrip";
import { useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import CalendarScreenHeader from "./Views/CalendarScreenHeader";
import placeholderIcon from "../../assets/images/placeholder-app-icon.svg";
import { useQuery } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import dayjs from "dayjs";
import AppLoader from "components/elements/AppLoader";
import {
  API_DATE_FORMAT,
  CALENDAR_RESERVATION_TABS,
  CALENDAR_RESERVATION_TYPE,
} from "utils/constants";
import { getWorkspaceSlotDuration, getWorkspaceSlotTime } from "utils/utils";

const CalendarScreen: FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const storedDate = sessionStorage.getItem("calendarSelectedDate");
    return storedDate ? new Date(storedDate) : new Date();
  });
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    const storedTab = sessionStorage.getItem("calendarSelectedTab");
    return storedTab ?? CALENDAR_RESERVATION_TYPE.DF_WORKSPACE;
  });
  const [showAnimation, setShowAnimation] = useState(false);
  const navigate = useNavigate();
  const { fetchWSMSlots } = useWSMApi();
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hideCalendarStrip =
    selectedTab === CALENDAR_RESERVATION_TYPE.EVENTS ||
    selectedTab === CALENDAR_RESERVATION_TYPE.WORKSHOP;

  useEffect(() => {
    const timer = setTimeout(() => setShowAnimation(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const tabIndex = CALENDAR_RESERVATION_TABS.findIndex(
      (tab) => tab.value === selectedTab,
    );
    tabRefs.current[tabIndex]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedTab]);

  useEffect(() => {
    sessionStorage.setItem("calendarSelectedDate", selectedDate.toISOString());
    sessionStorage.setItem("calendarSelectedTab", selectedTab);
  }, [selectedDate, selectedTab]);

  const { data: slotsData, isLoading } = useQuery({
    queryKey: ["get-wsm-slots", selectedDate, selectedTab],
    queryFn: () =>
      fetchWSMSlots({
        date: selectedDate ? dayjs(selectedDate).format(API_DATE_FORMAT) : "",
        type: selectedTab,
      }),
    retry: false,
  });

  const handleDateChange = (dateString: string): void => {
    setSelectedDate(new Date(dateString));
  };

  const handleViewClick = (slot: any) => {
    localStorage.setItem("selectedSlotData", JSON.stringify(slot));
    navigate(ROUTE_PATHS.WSM_RESERVATIONS);
  };

  return (
    <div className="wms-mobile-container">
      <div className="calendar-screen-header-sticky">
        <CalendarScreenHeader
          onlyMonthYearSelection={hideCalendarStrip}
          selectedDate={selectedDate}
          setSelectedDate={handleDateChange}
        />
        <div
          className={`calendar-strip-wrapper ${hideCalendarStrip ? "hide" : "show"}`}
        >
          <CalendarStrip
            selectedDate={selectedDate}
            setSelectedDate={handleDateChange}
          />
        </div>
        <div className="calendar-tab-container">
          {CALENDAR_RESERVATION_TABS.map((type, idx) => (
            <div
              key={type.value}
              className={`calendar-tab-container-item ${
                selectedTab === type.value ? "selected-tab" : ""
              }`}
              onClick={() => {
                setSelectedTab(type.value);
              }}
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
            >
              {type.name}
            </div>
          ))}
        </div>
      </div>

      {isLoading ? (
        <AppLoader />
      ) : slotsData?.data?.content?.length > 0 ? (
        <div className="calendar-screen-main-container">
          <div className="calendar-slot-list-container">
            {slotsData?.data?.content?.map((slot: any, index: number) => {
              return (
                <div key={index}>
                  <div
                    className="calendar-slot-list-card"
                    onClick={() => handleViewClick(slot)}
                  >
                    <div className="calendar-slot-info-main-container">
                      <div className="calendar-card-image-container">
                        <div className="calendar-card-time">
                          {getWorkspaceSlotTime(slot?.start_time)}
                        </div>
                        <div
                          className="calendar-card-image"
                          style={{
                            backgroundImage: `url(${slot?.slot_image})`,
                          }}
                        >
                          {slot?.event_type &&
                            slot?.service_type ===
                              CALENDAR_RESERVATION_TYPE.EVENTS && (
                              <div
                                className={`calendar-card-image-footer ${
                                  slot?.event_type === "IN_PERSON"
                                    ? "in-person"
                                    : "in-video"
                                }`}
                              >
                                {slot?.event_type === "IN_PERSON" ? (
                                  "In Person"
                                ) : (
                                  <img
                                    src={eventVideoIcon}
                                    alt="calendar-in-person-icon"
                                  />
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="calendar-card-info-container">
                        <div className="calendar-card-title">{slot?.title}</div>
                        <div className="calendar-card-text-container">
                          <div style={{ width: "100%" }}>
                            {slot?.instructor && (
                              <div>{`${slot?.instructor?.first_name} ${slot?.instructor?.last_name}`}</div>
                            )}
                            <div className="calendar-card-location">
                              <img src={locationIcon} alt="location-logo" />
                              {slot?.location}
                            </div>
                            <div>
                              {getWorkspaceSlotDuration(
                                slot?.start_time,
                                slot?.end_time,
                              )}
                            </div>
                          </div>
                          <button
                            className={`${"calendar-slot-reserve-btn"}${
                              showAnimation && index === 0 ? " animate" : ""
                            }`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleViewClick(slot);
                            }}
                          >
                            Show List
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="calendar-slot-card-divider"></div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="calendar-screen-placeholder-container">
          <img src={placeholderIcon} alt="placeholder-logo" />
          <p>Seat is no longer available. Please choose another</p>
        </div>
      )}
    </div>
  );
};

export default CalendarScreen;
